import { orderService } from '@/services'
import { mapGetters, mapActions } from 'vuex'
import { handleRequestError } from '@/services/apiErrorHandler'
import logger from '@/logger/index'
import Vue from 'vue'

let latestOrderCartRequest = 0

// Requests de carrinho em voo, por chave (id do carrinho, ou '' para a listagem).
// Products.vue e OrderCart.vue podem montar juntos e pedir o mesmo carrinho ao
// mesmo tempo — sem isso saem duas /get-carrinhos idênticas para o servidor.
const inFlightOrderCartRequests = new Map()

function findCart (carts, cartId) {
  return carts.find(cart => String(cart.id) === String(cartId))
}

function findProduct (cart, productId) {
  return cart && Array.isArray(cart.products)
    ? cart.products.find(product => String(product.id) === String(productId))
    : null
}

const DEFAULT_ORDER_CART_ERROR = 'Não foi possível carregar o carrinho.'
const MAX_ORDER_CART_ERROR_LENGTH = 120

/**
 * Aceita só mensagem curta, de linha única e sem marcação. Um 500 do Lumen com
 * APP_DEBUG ligado, um HTML de erro do nginx ou um stack trace chegam aqui como
 * string e seriam despejados na barra lateral — esses casos caem no texto padrão.
 */
function isSafeOrderCartMessage (message) {
  if (typeof message !== 'string') return false

  const trimmed = message.trim()

  return trimmed.length > 0 &&
    trimmed.length <= MAX_ORDER_CART_ERROR_LENGTH &&
    !/[<\r\n]/.test(trimmed)
}

function orderCartErrorMessage (error) {
  // Só o corpo devolvido pelo servidor. Erro de rede/timeout não tem `response`
  // e traria a mensagem técnica do axios em inglês ("Network Error").
  const data = error && error.response ? error.response.data : null
  const message = data && typeof data === 'object' ? data.message : data

  return isSafeOrderCartMessage(message) ? message.trim() : DEFAULT_ORDER_CART_ERROR
}

function unknownOutcomeError (error) {
  const publicError = error.response && error.response.data && typeof error.response.data === 'object'
    ? error.response.data
    : error
  const requestId = error.requestId || (error.correlation && error.correlation.requestId)
  const userMessage = requestId
    ? `Não foi possível confirmar o resultado da operação. Código de atendimento: ${requestId}`
    : 'Não foi possível confirmar o resultado da operação.'

  publicError.outcome_unknown = true
  publicError.request_id = requestId
  publicError.support_code = requestId
  publicError.user_message = userMessage
  publicError.message = userMessage

  return publicError
}

async function reconcileCartState (commit, cartId, error, matchesExpectedState) {
  try {
    const response = await orderService.getOrderCart(
      { id: cartId },
      { interactionId: error.interactionId || (error.correlation && error.correlation.interactionId) }
    )
    const carts = response && response.data
    const cart = Array.isArray(carts) ? findCart(carts, cartId) : null

    if (!Array.isArray(carts) || !matchesExpectedState(cart, carts)) {
      throw unknownOutcomeError(error)
    }

    commit(TYPES.SET_ORDERS, carts)
    if (cart) commit(TYPES.SELECT_ORDER, { id: cartId })
    return { cart, carts }
  } catch (reconciliationError) {
    if (reconciliationError && reconciliationError.outcome_unknown) throw reconciliationError
    throw unknownOutcomeError(error)
  }
}

async function reconcileAddition (commit, payload, expectedProducts, error) {
  const { cart } = await reconcileCartState(commit, payload.order, error, candidate => {
    return expectedProducts.every(expected => {
      const product = findProduct(candidate, expected.id)
      return product && Number(product.quantity) === Number(expected.quantity)
    })
  })

  return expectedProducts.map(expected => findProduct(cart, expected.id))
}

async function reconcileNewCart (commit, previousCartIds, params, error) {
  try {
    const response = await orderService.getOrderCart(
      {},
      { interactionId: error.interactionId || (error.correlation && error.correlation.interactionId) }
    )
    const carts = response && response.data
    if (!Array.isArray(carts)) throw unknownOutcomeError(error)

    const candidates = carts.filter(cart => !previousCartIds.has(String(cart.id)))
    const expectedName = String(params.name || '').trim()
    const expectedObs = String(params.obs || '').trim()
    const matching = candidates.filter(cart => {
      const nameMatches = !expectedName || String(cart.name || '').trim() === expectedName
      const obsMatches = String(cart.obs || '').trim() === expectedObs
      return nameMatches && obsMatches
    })

    if (matching.length !== 1) throw unknownOutcomeError(error)

    commit(TYPES.SET_ORDERS, carts)
    commit(TYPES.SELECT_ORDER, { id: matching[0].id })
    return matching[0]
  } catch (reconciliationError) {
    if (reconciliationError && reconciliationError.outcome_unknown) throw reconciliationError
    throw unknownOutcomeError(error)
  }
}

const TYPES = {
  SELECT_ORDER: 'SELECT_ORDER',
  REMOVE_SELECT_ORDER: 'REMOVE_SELECT_ORDER',
  SET_ORDERS: 'SET_ORDERS',
  SET_MORE_ORDERS: 'SET_MORE_ORDERS',
  SET_MORE_ORDER: 'SET_MORE_ORDER',
  SET_CHECKOUT_ORDER: 'SET_CHECKOUT_ORDER',
  SET_NEW_ORDER: 'SET_NEW_ORDER',
  REMOVE_PRODUCT_ORDER: 'REMOVE_PRODUCT_ORDER',
  CLEAR_ORDER: 'CLEAR_ORDER',
  REMOVE_ORDER: 'REMOVE_ORDER',
  UPDATE_ORDER_NAME: 'UPDATE_ORDER_NAME',
  UPDATE_ORDER_OBS: 'UPDATE_ORDER_OBS',
  ADD_PRODUCT_ORDER: 'ADD_PRODUCT_ORDER',
  ADD_MORE_PRODUCTS_ORDER: 'ADD_MORE_PRODUCTS_ORDER',
  ADD_PART_PRODUCT_ORDER: 'ADD_PART_PRODUCT_ORDER',
  UPDATE_QTD_PRODUCT: 'UPDATE_QTD_PRODUCT',
  SET_STATUS_TABLE: 'SET_STATUS_TABLE',
  REMOVE_STATUS_TABLE: 'REMOVE_STATUS_TABLE',
  SET_LOADING_ORDER_CART: 'SET_LOADING_ORDER_CART',
  SET_FETCHING_ORDER_CART: 'SET_FETCHING_ORDER_CART',
  START_ORDER_CART_REQUEST: 'START_ORDER_CART_REQUEST',
  FINISH_ORDER_CART_REQUEST: 'FINISH_ORDER_CART_REQUEST',
  SET_ORDER_CART_LOADED: 'SET_ORDER_CART_LOADED',
  SET_ORDER_CART_ERROR: 'SET_ORDER_CART_ERROR',
}

const state = () => ({
  items: [],
  selectedOrder: null,
  statusTable: false,
  loadingOrderCart: false,
  isFetchingOrderCart: false,
  orderCartPendingRequests: 0,
  // Vira true na primeira resposta do getOrderCart, mesmo que o carrinho volte
  // vazio. Antes disso não dá pra dizer se o carrinho está vazio ou se ainda
  // não chegou — e é essa diferença que a tela precisa mostrar.
  orderCartLoaded: false,
  orderCartError: null,
})

const getters = {
  orders: state => {
    return state.items
  },

  total: state => {
    return state.items.length
  },

  totalInProgress: state => {
    return state.items.filter(order => order.status === 'Em andamento').length
  },

  totalFinished: state => {
    return state.items.filter(order => order.status === 'Finalizado').length
  },

  totalRecords: state => {
    return state.items[0]?.total_records
  },

  getSelectedOrder: state => {
    return state.selectedOrder
  },

  getStatusTable: state => {
    return state.statusTable
  },

  isLoadingOrderCart: state => {
    return state.loadingOrderCart
  },

  isFetchingOrderCart: state => {
    return state.isFetchingOrderCart
  },

  hasLoadedOrderCart: state => {
    return state.orderCartLoaded
  },

  getOrderCartError: state => {
    return state.orderCartError
  },
}

const actions = {
  selectOrder ({ commit, dispatch }, params) {
    // A carga automática do carrinho (getOrderCart) passa keepSelectedParts pra
    // NÃO zerar o conjunto de peças que o usuário está montando em outra aba —
    // senão a lista some e a inserção do conjunto é cancelada no meio. A troca
    // manual de pedido (OrdersDetails) continua limpando, que é o esperado.
    if (!params.keepSelectedParts) {
      dispatch('products/removeSelectedParts', {}, { root: true })
    }
    commit(TYPES.SET_STATUS_TABLE)
    commit(TYPES.SELECT_ORDER, params)
  },

  removeSelectOrder ({ commit }) {
    commit(TYPES.REMOVE_SELECT_ORDER)
    // this.dispatch('setFeedback', ({ title: 'Este pedido já está finalizado', message: '', type: 'error' }))
  },

  addPushOrder ({ commit }, params) {
    commit(TYPES.SET_MORE_ORDER, params)
  },

  getAllOrders ({ commit }, params) {
    let order = {
      ...params,
    }
    return new Promise((resolve, reject) => {
      orderService
        .getAll(order)
        .then(({ data }) => {
          commit(TYPES.SET_ORDERS, data)
          resolve(data)
        })
        .catch(error => handleRequestError(error, reject))
    })
  },

  getMoreOrders ({ commit }, params) {
    let order = {
      ...params,
    }
    return new Promise((resolve, reject) => {
      orderService
        .getAll(order)
        .then(({ data }) => {
          commit(TYPES.SET_MORE_ORDERS, data)
          resolve(data)
        })
        .catch(error => handleRequestError(error, reject))
    })
  },

  getOrderCart ({ commit, dispatch, state }, params) {
    const order = {
      ...params,
    }

    // Já existe uma request idêntica em voo: reaproveita em vez de duplicar.
    const requestKey = order && order.id ? String(order.id) : ''
    const inFlightRequest = inFlightOrderCartRequests.get(requestKey)
    if (inFlightRequest) return inFlightRequest

    const requestSequence = ++latestOrderCartRequest
    commit(TYPES.START_ORDER_CART_REQUEST)

    const request = orderService
      .getOrderCart(order)
      .then(response => {
        // A carga inicial terminou: daqui pra frente "sem produtos" significa
        // carrinho vazio de verdade, e não resposta pendente.
        commit(TYPES.SET_ORDER_CART_LOADED, true)

        if (!response || !response.data) return state.items || []

        const { data } = response

        // Uma resposta antiga não pode sobrescrever uma seleção mais recente.
        if (requestSequence !== latestOrderCartRequest) return data

        commit(TYPES.SET_ORDERS, data)
        const selectedOrderId = order && order.id ? order.id : data[0]?.id

        if (!selectedOrderId) return data

        return dispatch('selectOrder', { id: selectedOrderId, keepSelectedParts: true }).then(() => data)
      })
      .catch(error => {
        // Uma falha antiga não pode marcar erro por cima de um request mais novo.
        if (requestSequence === latestOrderCartRequest) {
          commit(TYPES.SET_ORDER_CART_ERROR, orderCartErrorMessage(error))
        }
        return handleRequestError(error)
      })
      .finally(() => {
        inFlightOrderCartRequests.delete(requestKey)
        commit(TYPES.FINISH_ORDER_CART_REQUEST)
      })

    inFlightOrderCartRequests.set(requestKey, request)

    return request
  },

  async checkoutOrder ({ commit }, params) {
    try {
      await orderService.checkout(params)
      commit(TYPES.SET_CHECKOUT_ORDER, params)
      return params
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      await reconcileCartState(commit, params.id, error, cart => !cart)
      return { ...params, status: 'ok', reconciled: true }
    }
  },

  async addNewOrder ({ commit, state }, params) {
    const previousCartIds = new Set(state.items.map(cart => String(cart.id)))

    try {
      const { data } = await orderService.add(params)
      commit(TYPES.SET_NEW_ORDER, data)
      return data
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      const cart = await reconcileNewCart(commit, previousCartIds, params, error)
      return { ...cart, status: 'ok', reconciled: true }
    }
  },

  async clearOrder ({ commit }, params) {
    try {
      const { data } = await orderService.clearOrder(params)
      commit(TYPES.CLEAR_ORDER, [])
      return data
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      await reconcileCartState(commit, params.id, error, cart => {
        return cart && Array.isArray(cart.products) && cart.products.length === 0
      })
      return { status: 'ok', reconciled: true }
    }
  },

  async deleteOrder ({ commit }, params) {
    try {
      await orderService.remove(params)
      commit(TYPES.REMOVE_ORDER, params)
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      await reconcileCartState(commit, params.id, error, cart => !cart)
      commit(TYPES.REMOVE_ORDER, params)
      return { status: 'ok', reconciled: true }
    }
  },

  async updateOrderName ({ commit }, params) {
    try {
      await orderService.updateOrderName(params)
      commit(TYPES.UPDATE_ORDER_NAME, params)
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      await reconcileCartState(commit, params.id, error, cart => {
        return cart && String(cart.name || '') === String(params.name || '')
      })
      return { status: 'ok', reconciled: true }
    }
  },

  async updateOrderObs ({ commit }, params) {
    try {
      await orderService.updateOrderObs(params)
      commit(TYPES.UPDATE_ORDER_OBS, params)
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      await reconcileCartState(commit, params.id, error, cart => {
        return cart && String(cart.obs || '') === String(params.obs || '')
      })
      return { status: 'ok', reconciled: true }
    }
  },

  async removeProduct ({ commit }, params) {
    try {
      await orderService.removeProduct(params)
      commit(TYPES.REMOVE_PRODUCT_ORDER, params)
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      await reconcileCartState(commit, params.id, error, cart => {
        return cart && !findProduct(cart, params.product_id)
      })
      return { status: 'ok', reconciled: true }
    }
  },

  async updateProductQuantity ({ commit, state }, params) {
    const product = {
      product: params.product,
      quantity: params.quantity,
      order: state.selectedOrder.id,
    }

    let data
    try {
      ({ data } = await orderService.updateProductQuantity(product))
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      await reconcileCartState(commit, product.order, error, cart => {
        const candidate = findProduct(cart, params.product.id)
        return candidate && Number(candidate.quantity) === Number(params.quantity)
      })
      return { status: 'ok', reconciled: true }
    }

    // A API responde 200 sem `status: 'ok'` quando recusa os dados (ex.:
    // "Informe a Fração do produto"); sem isso a tela mostrava sucesso sem
    // ter gravado nada.
    if (!data || data.status !== 'ok') {
      throw (data && typeof data === 'object' ? data : { message: 'Não foi possível atualizar a quantidade.' })
    }
    commit(TYPES.UPDATE_QTD_PRODUCT, data.product)
  },

  async addProduct ({ commit, state }, params) {
    const product = {
      ...params.product,
      quantity: params.quantity,
      order: state.selectedOrder.id,
    }

    try {
      const { data } = await orderService.addProduct(product)
      if (data.status === 'ok') {
        commit(TYPES.ADD_PRODUCT_ORDER, data.product)
      }
      return data
    } catch (error) {
      if (!error.outcomeUnknown) return handleRequestError(error)

      const [confirmedProduct] = await reconcileAddition(
        commit,
        product,
        [{ id: product.id, quantity: product.quantity }],
        error
      )
      return { status: 'ok', product: confirmedProduct, reconciled: true }
    }
  },

  async addPartProducts ({ commit, state, dispatch }, params) {
    const payload = {
      products: [...params],
      order: state.selectedOrder.id,
    }

    try {
      const { data } = await orderService.addPartProduct(payload)

      // Filtra os que passaram e os que falharam
      const okProducts = data
        .filter(i => i.status === 'ok')
        .map(item => {
          const prod = item.product

          return {
            id: prod.id,
            description: prod.description,
            quantity: prod.quantity,
            price: prod.price,
            list_type: prod.list_type,
            product_ipi: prod.product_ipi,
            ipi_aliquota: prod.ipi_aliquota,
            total_price: prod.total_price,
            total_price_with_ipi: prod.total_price_with_ipi,
            fraction: prod.fraction,
            sequence: prod.sequence ?? null,
            date: prod.date ?? null,
            image: prod.image,
            stock: prod.stock,
          }
        })
      // Commita só os OK
      if (okProducts.length) {
        commit(TYPES.ADD_MORE_PRODUCTS_ORDER, okProducts)
      }

      const failedEntries = data
        .filter(i => i.status !== 'ok')
        .map(i => ({ code: i.product.id ?? i.product.product_id, message: i.mensagem }))
      if (failedEntries.length) {
        const codes = failedEntries.map(f => f.code).join(', ')

        dispatch(
          'setFeedback',
          {
            title: 'Alguns produtos não foram inseridos',
            message: `Códigos com erro: ${codes}`,
            type: 'error',
          },
          { root: true }
        )
        // logger.error('addPartProducts failed entries:', failedEntries)
      }

      return { okProducts, failedEntries }
    } catch (error) {
      logger.error(error)
      if (!error.outcomeUnknown) return handleRequestError(error)

      const expectedProducts = payload.products.map(product => ({
        id: product.id ?? product.product_id,
        quantity: product.quantity,
      }))
      const confirmedProducts = await reconcileAddition(commit, payload, expectedProducts, error)
      return { okProducts: confirmedProducts, failedEntries: [], reconciled: true }
    }
  },

  removeStatusTable ({ commit }) {
    commit(TYPES.REMOVE_STATUS_TABLE)
  },
}

const mutations = {
  [TYPES.SET_ORDERS] (state, payload) {
    state.items = [...payload]
  },

  [TYPES.SET_MORE_ORDER] (state, payload) {
    state.items.push(payload)
  },

  [TYPES.SET_MORE_ORDERS] (state, payload) {
    state.items.push(...payload)
  },

  [TYPES.SET_CHECKOUT_ORDER] (state, payload) {
    state.items.find(order => order.id === payload.id).status = 'Finalizado'
    state.selectedOrder = state.items.find(
      order => order.status === 'Em andamento'
    )
  },

  [TYPES.SET_NEW_ORDER] (state, payload) {
    payload.status = 'Em andamento'
    state.items.push(payload)
    state.selectedOrder = state.items.find(order => order.id === payload.id)
  },

  [TYPES.REMOVE_PRODUCT_ORDER] (state, payload) {
    const order = state.items.find(order => order.id === payload.id)
    const productIndex = order.products.findIndex(
      product => product.id === payload.product_id
    )
    if (productIndex !== -1) {
      Vue.set(order.products, productIndex, undefined)
      order.products = order.products.filter(product => product !== undefined)
    }
  },

  [TYPES.CLEAR_ORDER] (state, payload) {
    state.selectedOrder.products = payload
  },

  [TYPES.REMOVE_ORDER] (state, payload) {
    state.selectedOrder = null
  },

  [TYPES.UPDATE_ORDER_NAME] (state, payload) {
    state.items.find(order => order.id === payload.id).name = payload.name
  },

  [TYPES.UPDATE_ORDER_OBS] (state, payload) {
    state.items.find(order => order.id === payload.id).obs = payload.obs
  },

  [TYPES.SELECT_ORDER] (state, payload) {
    state.selectedOrder = state.items.find(order => order.id === payload.id)
  },

  [TYPES.ADD_PRODUCT_ORDER] (state, payload) {
    state.selectedOrder.products.push(payload)
  },

  [TYPES.ADD_MORE_PRODUCTS_ORDER] (state, payload) {
    payload.forEach(prod => {
      const idx = state.selectedOrder.products.findIndex(p => p.id === prod.id)

      if (idx === -1) {
        state.selectedOrder.products.push(prod)
      } else {
        // se existir substitui completamente pelo novo objeto
        state.selectedOrder.products.splice(idx, 1, prod)
      }
    })
  },

  [TYPES.UPDATE_QTD_PRODUCT] (state, payload) {
    let product = state.selectedOrder.products.find(
      products => products.id === payload.id
    )
    if (product) {
      product.quantity = payload.quantity
      product.price = payload.price
      product.list_type = payload.list_type
      product.total_price = Number(payload.total_price.toFixed(2))
      product.total_price_with_ipi = Number(
        payload.total_price_with_ipi.toFixed(2)
      )
    }
  },
  [TYPES.SET_STATUS_TABLE] (state) {
    state.statusTable = true
  },
  [TYPES.REMOVE_STATUS_TABLE] (state) {
    state.statusTable = false
  },

  [TYPES.REMOVE_SELECT_ORDER] (state) {
    state.selectedOrder = null
  },

  [TYPES.SET_LOADING_ORDER_CART] (state, payload) {
    state.loadingOrderCart = payload
  },

  [TYPES.SET_FETCHING_ORDER_CART] (state, payload) {
    state.isFetchingOrderCart = payload
  },

  [TYPES.START_ORDER_CART_REQUEST] (state) {
    state.orderCartPendingRequests += 1
    state.loadingOrderCart = true
    state.isFetchingOrderCart = true
    // Nova tentativa em andamento: volta pro estado de carregando.
    state.orderCartError = null
  },

  [TYPES.FINISH_ORDER_CART_REQUEST] (state) {
    state.orderCartPendingRequests = Math.max(0, state.orderCartPendingRequests - 1)
    state.loadingOrderCart = state.orderCartPendingRequests > 0
    state.isFetchingOrderCart = state.orderCartPendingRequests > 0
  },

  [TYPES.SET_ORDER_CART_LOADED] (state, payload) {
    state.orderCartLoaded = payload
  },

  [TYPES.SET_ORDER_CART_ERROR] (state, payload) {
    state.orderCartError = payload
  },
}

export default {
  computed: {
    ...mapGetters('auth', ['getUserName']),
  },
  methods: {
    ...mapActions(['setFeedback']),
  },
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
